import {
  formatCommercialAmount,
  commercialInputValue,
} from "../../helpers/commercialAmounts";

/**
 * Pure presentation model for Material records (view + edit + list).
 *
 * Data contract (unchanged): `materials.material_type` is 'Product' or
 * 'Service'. Services reuse the Materials architecture, with `unit_cost`
 * holding the service rate. Product is shown to people as "Material".
 */

export const MATERIAL_TYPE = Object.freeze({
  PRODUCT: "Product",
  SERVICE: "Service",
});

export function isServiceType(type) {
  return String(type || "").trim().toLowerCase() === "service";
}

export function materialTypeLabel(type) {
  const value = String(type || "").trim();
  if (!value || value.toLowerCase() === "product") return "Material";
  if (isServiceType(value)) return "Service";
  return value;
}

/** Materials have a unit cost; Services have a unit rate. */
export function costLabel(type) {
  return isServiceType(type) ? "Unit Rate" : "Unit Cost";
}

/** Services are not stocked, so inventory only applies to everything else. */
export function usesInventory(type) {
  return !isServiceType(type);
}

export function materialTypeOptions(currentType) {
  const options = [
    { value: MATERIAL_TYPE.PRODUCT, label: "Material" },
    { value: MATERIAL_TYPE.SERVICE, label: "Service" },
  ];
  const current = String(currentType || "").trim();
  if (
    current &&
    current.toLowerCase() !== "product" &&
    current.toLowerCase() !== "service"
  ) {
    options.unshift({ value: current, label: current });
  }
  return options;
}

export function hasLoadedField(material, field) {
  return Boolean(material) && Object.prototype.hasOwnProperty.call(material, field);
}

/** "$12.50", or an em dash when unknown. Never "$NaN". */
export function formatCost(material) {
  if (!hasLoadedField(material, "unit_cost")) return "—";
  return formatCommercialAmount(material.unit_cost);
}

export function formatStock(material) {
  if (
    !hasLoadedField(material, "current_inventory") ||
    material.current_inventory == null ||
    material.current_inventory === ""
  ) {
    return "—";
  }
  const parsed = Number(material.current_inventory);
  return Number.isFinite(parsed) ? String(parsed) : "—";
}

export function emptyMaterialForm() {
  return {
    material_name: "",
    sku: "",
    description: "",
    unit_of_measure: "",
    unit_cost: "",
    current_inventory: "",
    material_type: MATERIAL_TYPE.PRODUCT,
    active: true,
  };
}

export function formFromMaterial(material) {
  return {
    material_name: material?.material_name || "",
    sku: material?.sku || "",
    description: material?.description || "",
    unit_of_measure: material?.unit_of_measure || "",
    unit_cost: commercialInputValue(material?.unit_cost),
    current_inventory: commercialInputValue(material?.current_inventory),
    material_type: material?.material_type || "",
    active: material?.active !== false,
  };
}

/** Stable 0-3 bucket so a material keeps the same placeholder tone. */
export function placeholderTone(name) {
  const text = String(name || "");
  let hash = 0;
  for (let i = 0; i < text.length; i += 1) {
    hash = (hash * 31 + text.charCodeAt(i)) >>> 0;
  }
  return ["a", "b", "c", "d"][hash % 4];
}

/**
 * Type filter. Uses the real `material_type` values (no second classification):
 * Product (shown as "Material") and Service. Any other value that exists in
 * the loaded data gets its own option so nothing is silently hidden.
 */
export const TYPE_FILTER_ALL = "all";
const TYPE_FILTER_NONE = "__none__";

export function typeFilterKey(type) {
  const value = String(type ?? "").trim().toLowerCase();
  return value || TYPE_FILTER_NONE;
}

export function typeFilterOptions(materials) {
  const options = [
    { key: TYPE_FILTER_ALL, label: "All Types" },
    { key: "product", label: "Material" },
    { key: "service", label: "Service" },
  ];
  const known = new Set(options.map((option) => option.key));
  const extras = new Map();

  for (const material of materials || []) {
    const key = typeFilterKey(material?.material_type);
    if (known.has(key) || extras.has(key)) continue;
    extras.set(key, key === TYPE_FILTER_NONE ? "Unspecified" : String(material.material_type).trim());
  }

  const sorted = [...extras.entries()]
    .sort((a, b) => a[1].localeCompare(b[1]))
    .map(([key, label]) => ({ key, label }));

  return [...options, ...sorted];
}

export function matchesTypeFilter(material, filterKey) {
  if (!filterKey || filterKey === TYPE_FILTER_ALL) return true;
  return typeFilterKey(material?.material_type) === filterKey;
}
